/**
 * Settings > Performance > Speed boost: which graphics cards Paige may use.
 *
 * Each card is a tile: its drawing, its name, its memory as the big number, and what
 * Paige runs on it. Selected means used. One card is a fact, not a choice. Turning
 * every card off is allowed: Speed boost is then off and documents are read on the processor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Zap, ZapOff } from 'lucide-react';
import type {
  GraphicsCardTenant,
  GraphicsCardView,
  GraphicsCardsAllowed,
  GraphicsCardsView,
} from '../shared/desktop-api';
import './graphics-cards.css';

/** The feature names Performance already uses for this work (customer-health-report.ts), and Pilot mode's "closer look". */
const tenantTitle: Record<GraphicsCardTenant, string> = {
  reader: 'Document details',
  reasoner: 'Closer look',
  ocr: 'Searchable text',
};
const tenantOrder: GraphicsCardTenant[] = ['reader', 'reasoner', 'ocr'];

const CHOICE_NOT_SAVED =
  'Could not save that choice. Your previous setting is unchanged; try again.';

const isUsed = (allowed: GraphicsCardsAllowed, card: GraphicsCardView) =>
  allowed === 'all' || allowed.includes(card.uuid);

/** The model, without the maker's name the driver puts in front ("NVIDIA GeForce RTX 4090" → "GeForce RTX 4090"). */
const cardName = (name: string) => name.replace(/^nvidia\s+/i, '').trim() || name;

/** Whole gigabytes, as the card is sold (24,564 MiB is a 24 GB card). */
const gigabytes = (mib: number | null) =>
  mib === null ? null : Math.max(1, Math.round(mib / 1024));

/** The saved choice and this computer's cards; null until read, or when this view has no bridge for it. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function useGraphicsCards() {
  const [view, setView] = useState<GraphicsCardsView | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  /** The card just turned on, so only its fans spin up: motion for the change, never on opening. */
  const [turnedOn, setTurnedOn] = useState<string | null>(null);
  const live = useRef(false);
  const pending = useRef(false);
  const available = Boolean(
    window.capturePointDesktop?.getGraphicsCards && window.capturePointDesktop?.setGraphicsCards,
  );
  useEffect(() => {
    live.current = true;
    const api = window.capturePointDesktop;
    if (api?.getGraphicsCards)
      void api
        .getGraphicsCards()
        .then((next) => {
          if (live.current) setView(next);
        })
        .catch(() => {
          if (live.current) setError('Could not load your graphics cards.');
        });
    return () => {
      live.current = false;
    };
  }, []);
  /** Turn one card on or off. Shown at once; put back if it could not be saved. */
  const toggle = useCallback(
    async (card: GraphicsCardView) => {
      const api = window.capturePointDesktop;
      if (!api?.setGraphicsCards || !view || pending.current) return;
      const used = view.cards.filter((each) => isUsed(view.allowed, each)).map((each) => each.uuid);
      const next = isUsed(view.allowed, card)
        ? used.filter((id) => id !== card.uuid)
        : [...used, card.uuid];
      const allowed: GraphicsCardsAllowed = view.cards.every((each) => next.includes(each.uuid))
        ? 'all'
        : next;
      const previous = view;
      pending.current = true;
      setSaving(true);
      setError(null);
      setView({ ...view, allowed });
      setTurnedOn(next.includes(card.uuid) ? card.uuid : null);
      try {
        const saved = await api.setGraphicsCards(allowed);
        if (live.current) {
          setView(saved);
          setChanged(true);
        }
      } catch {
        if (live.current) {
          setView(previous);
          setError(CHOICE_NOT_SAVED);
        }
      } finally {
        pending.current = false;
        if (live.current) setSaving(false);
      }
    },
    [view],
  );
  const cards = view?.cards ?? [];
  const processorOnly =
    cards.length > 0 &&
    view?.allowed !== 'all' &&
    cards.every((card) => !isUsed(view!.allowed, card));
  return { view, available, saving, error, changed, toggle, processorOnly, turnedOn };
}

export type GraphicsCardsState = ReturnType<typeof useGraphicsCards>;

/** The row of card tiles under the Speed boost heading. */
export function GraphicsCardTiles({ state }: { state: GraphicsCardsState }) {
  const { view, saving, error, changed, toggle, processorOnly, turnedOn } = state;
  if (!view || view.cards.length === 0)
    return error ? (
      <p
        className="graphics-cards-error"
        role="alert"
        data-telemetry="error:graphics-cards-load-failed"
      >
        {error}
      </p>
    ) : null;
  const choice = view.cards.length > 1;
  const note = processorOnly
    ? `Documents are read on the processor only.${changed ? ' Applies to documents processed next.' : ''}`
    : changed
      ? 'Applies to documents processed next.'
      : null;
  // Identical cards (three "RTX 5060 Ti") are told apart by their place in this computer's order,
  // drawn as a small number on each card's picture; a computer whose cards all differ shows none.
  const names = view.cards.map((card) => cardName(card.name));
  const numbered = new Set(names).size < names.length;
  const tiles = view.cards.map((card, index) => (
    <GraphicsCardTile
      key={card.uuid}
      card={card}
      number={numbered ? index + 1 : null}
      used={isUsed(view.allowed, card)}
      turnedOn={turnedOn === card.uuid}
      choice={choice}
      saving={saving}
      onToggle={() => void toggle(card)}
    />
  ));
  // The row's shape follows how many cards there are: two across, three across, two by two for
  // four, and an even grid after that, so no card sits alone on a row it does not fill.
  const count = Math.min(view.cards.length, 5);
  return (
    <div className={`graphics-cards${processorOnly ? ' processor-only' : ''}`}>
      {choice ? (
        <fieldset
          className="graphics-card-tiles"
          data-count={count}
          aria-busy={saving || undefined}
          aria-describedby={note ? 'graphics-cards-note' : undefined}
        >
          <legend className="visually-hidden">Graphics cards Paige uses</legend>
          {tiles}
        </fieldset>
      ) : (
        <div
          className="graphics-card-tiles"
          data-count={count}
          role="group"
          aria-label="Graphics card"
        >
          {tiles}
        </div>
      )}
      {note && (
        <p id="graphics-cards-note" className="graphics-cards-note" role="status">
          {processorOnly && <ZapOff size={14} aria-hidden="true" />}
          {note}
        </p>
      )}
      {error && (
        <p
          className="graphics-cards-error"
          role="alert"
          data-telemetry={
            error === CHOICE_NOT_SAVED
              ? 'error:graphics-cards-save-failed'
              : 'error:graphics-cards-load-failed'
          }
        >
          {error}
        </p>
      )}
    </div>
  );
}

function GraphicsCardTile({
  card,
  number,
  used,
  turnedOn,
  choice,
  saving,
  onToggle,
}: {
  card: GraphicsCardView;
  number: number | null;
  used: boolean;
  turnedOn: boolean;
  choice: boolean;
  saving: boolean;
  onToggle: () => void;
}) {
  const memory = gigabytes(card.totalMiB);
  // Several roles stay one quiet line: each role once (a card may hold more than one reader), and
  // a line too long for its tile ends in "…" with the whole of it on hover.
  const runs =
    used && card.tenants
      ? tenantOrder
          .filter((kind) => card.tenants!.includes(kind))
          .map((kind) => tenantTitle[kind])
          .join(' · ')
      : '';
  const body = (
    <>
      <span className="graphics-card-picture">
        <GraphicsCardArt />
        {number !== null && (
          <span className="graphics-card-number" aria-hidden="true">
            {number}
          </span>
        )}
      </span>
      <span className="graphics-card-copy">
        <span className="graphics-card-name">{cardName(card.name)}</span>
        {memory !== null && (
          <span className="graphics-card-memory">
            <strong>{memory}</strong> GB
          </span>
        )}
        {/* What runs on it is a fact the engine reported; an unused card says so instead. */}
        {!used ? (
          <span className="graphics-card-runs off">
            <ZapOff size={13} aria-hidden="true" />
            <span>Off</span>
          </span>
        ) : runs ? (
          <span className="graphics-card-runs on">
            <Zap size={13} aria-hidden="true" />
            <span title={runs}>{runs}</span>
          </span>
        ) : card.tenants ? (
          <span className="graphics-card-runs ready">
            <span>Ready</span>
          </span>
        ) : null}
      </span>
    </>
  );
  const label = `${cardName(card.name)}${number !== null ? `, card ${number}` : ''}${memory !== null ? `, ${memory} GB` : ''}`;
  if (!choice)
    return (
      <div className="graphics-card-tile is-fact is-used" aria-label={label}>
        {body}
      </div>
    );
  return (
    <label
      className={`graphics-card-tile${used ? ' is-used' : ''}${used && turnedOn ? ' turned-on' : ''}`}
    >
      <input
        type="checkbox"
        checked={used}
        disabled={saving}
        onChange={onToggle}
        aria-label={`Use ${label}`}
      />
      {body}
      <span className="graphics-card-check" aria-hidden="true">
        {used && <Check size={12} strokeWidth={3} />}
      </span>
    </label>
  );
}

/** A graphics card in the Performance line style: board, two fans, bracket and connector. Decorative. */
export function GraphicsCardArt() {
  return (
    <svg className="graphics-card-art" viewBox="0 0 120 76" aria-hidden="true">
      <path className="gca-bracket" d="M9 10h7v52H9z" />
      <g className="art-paper">
        <rect x="16" y="12" width="96" height="46" rx="6" />
      </g>
      <path className="gca-pins" d="M40 58v7h46v-7" />
      <path className="art-ink gca-teeth" d="M45 61v3m6-3v3m6-3v3m6-3v3m6-3v3m6-3v3" />
      {[42, 86].map((cx) => (
        <g key={cx} className="gca-fan" transform={`translate(${cx} 35)`}>
          <circle className="gca-fan-ring" r="15" />
          <g className="gca-blades">
            {[0, 72, 144, 216, 288].map((turn) => (
              <path key={turn} d="M0-4.5q5.5-3 3.5-9" transform={`rotate(${turn})`} />
            ))}
          </g>
          <circle className="gca-hub" r="3.6" />
        </g>
      ))}
      <path className="gca-light" d="M23 17.5h9" />
    </svg>
  );
}
